import { supabase } from "@/integrations/supabase/client";
import type { StageKey, StageOutcome } from "@/lib/stages";

export type ApplicationStage = {
  id: string;
  application_id: string;
  user_id: string;
  stage_key: StageKey;
  custom_label: string | null;
  sequence: number;
  entered_at: string;
  outcome: StageOutcome;
  outcome_at: string | null;
  notes: string | null;
  interviewers: Array<{ name: string; role?: string }>;
  created_at: string;
  updated_at: string;
};

export const stagesQueryKey = (userId: string) => ["application-stages", userId] as const;

function rowToStage(row: any): ApplicationStage {
  return {
    id: row.id,
    application_id: row.application_id,
    user_id: row.user_id,
    stage_key: row.stage_key,
    custom_label: row.custom_label ?? null,
    sequence: row.sequence ?? 0,
    entered_at: row.entered_at,
    outcome: row.outcome,
    outcome_at: row.outcome_at ?? null,
    notes: row.notes ?? null,
    interviewers: Array.isArray(row.interviewers) ? row.interviewers : [],
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

export async function fetchAllStages(userId: string): Promise<ApplicationStage[]> {
  const { data, error } = await supabase
    .from("application_stages" as never)
    .select("*")
    .eq("user_id", userId)
    .order("sequence", { ascending: true });
  if (error) throw error;
  return ((data ?? []) as any[]).map(rowToStage);
}

export async function insertStage(
  userId: string,
  applicationId: string,
  input: {
    stage_key: StageKey;
    sequence: number;
    custom_label?: string | null;
    notes?: string | null;
  },
): Promise<ApplicationStage> {
  const { data, error } = await supabase
    .from("application_stages" as never)
    .insert({
      user_id: userId,
      application_id: applicationId,
      stage_key: input.stage_key,
      sequence: input.sequence,
      custom_label: input.custom_label ?? null,
      notes: input.notes ?? null,
      outcome: "pending",
    } as never)
    .select("*")
    .single();
  if (error) throw error;
  return rowToStage(data);
}

export async function updateStage(
  userId: string,
  id: string,
  patch: Partial<{
    custom_label: string | null;
    notes: string | null;
    outcome: StageOutcome;
    outcome_at: string | null;
    interviewers: Array<{ name: string; role?: string }>;
    stage_key: StageKey;
  }>,
) {
  const dbPatch: Record<string, unknown> = { ...patch };
  if (patch.outcome && patch.outcome !== "pending" && patch.outcome_at === undefined) {
    dbPatch.outcome_at = new Date().toISOString();
  }
  if (patch.outcome === "pending" && patch.outcome_at === undefined) {
    dbPatch.outcome_at = null;
  }
  const { error } = await supabase
    .from("application_stages" as never)
    .update(dbPatch as never)
    .eq("id", id)
    .eq("user_id", userId);
  if (error) throw error;
}

export async function deleteStage(userId: string, id: string) {
  const { error } = await supabase
    .from("application_stages" as never)
    .delete()
    .eq("id", id)
    .eq("user_id", userId);
  if (error) throw error;
}

export async function setCurrentStage(
  userId: string,
  applicationId: string,
  stageId: string | null,
) {
  const { error } = await supabase
    .from("application_tracking")
    .update({ current_stage_id: stageId } as never)
    .eq("id", applicationId)
    .eq("user_id", userId);
  if (error) throw error;
}
